import React, { FunctionComponent, useState } from "react";
import { FiCamera } from "@react-icons/all-files/fi/FiCamera";
import { FiEdit2 } from "@react-icons/all-files/fi/FiEdit2";
import { FiEdit3 } from "@react-icons/all-files/fi/FiEdit3";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { RenderType } from "../../../../Enums/RenderType";
import { IBrand } from "../../../../Models/IBrand";
import {
    IRetailMatchJobResult,
    IStoreScoreRecord,
} from "../../../../Models/IRetailMatch";
import { ReportBackLink, ReportH3 } from "../../../Common";
import { ReportCard, ReportScrollableSection } from "../../../Common/";
import { SiteDetailPhotoButtons } from "../Styled/Buttons";
import {
    IntroGridContentBlockOne,
    RetailMatchIntroGrid,
} from "../Styled/RetailGrid";
import {
    RetailMatchIntroBrandingContainer,
    RetailMatchIntroBrandLogo,
    RetailMatchIntroBrandLogoWrapper,
} from "../Styled/RetailMatchIntroStyles";
import { overviewLinkURL } from "../Util/RetailMatchUtil";
import RetailMatchIntroAddPhoto from "./IntroModals/RetailMatchIntroAddPhoto";
import RetailMatchIntroAddSiteDetails from "./IntroModals/RetailMatchIntroAddSiteDetails";
import RetailMatchIntroEditBrandLogo from "./IntroModals/RetailMatchIntroEditBrandLogo";
import { RetailMatchIntroPhotoCarousel } from "./IntroModals/RetailMatchIntroPhotoCarousel";
import { RetailMatchIntroText } from "./RetailMatchIntroText";
import {
    IRetailMatchReportContainerRouteParams,
    IRetailMatchReportContainerShareRouteParams,
} from "./RetailMatchReportContainer";

interface RetailMatchIntroductionProps {
    multiBrand: boolean;
    brand: IBrand;
    jobResult: IRetailMatchJobResult;
    locationAddress: string;
    isSharing?: boolean;
    benchmarkAddressOverride: IStoreScoreRecord;
    renderType: RenderType;
}

const buxtonLogo = "/Images/NEW_UI/rebrand/svg/Buxton-Green.svg";

export const RetailMatchIntroduction: FunctionComponent<RetailMatchIntroductionProps>
    = (props: RetailMatchIntroductionProps) => {
        /** props */
        const {
            jobResult: { job, chartData },
            isSharing,
        } = props;
        /** state */
        const [showAddSiteDetails, setShowAddSiteDetails]
            = useState<boolean>(false);
        const [showAddSitePhotos, setShowAddSitePhotos] = useState<boolean>(false);
        const [showCarouselModal, setShowCarouselModal] = useState<boolean>(false);
        const [showEditLogo, setShowEditLogo] = useState<boolean>(false);

        let { brandId } = useParams<IRetailMatchReportContainerRouteParams>();

        //If sharing, we need to get the brandId from the route differently
        if (isSharing) {
            const match
                = useRouteMatch<IRetailMatchReportContainerShareRouteParams>(`/:brandId`);
            brandId = match?.params?.brandId;
        }

        const history = useHistory();

        const onBackToOverviewClick = () => {
            const returnUrl = overviewLinkURL(history.location.pathname, isSharing, job.jobId);

            history.push(returnUrl);
        };

        return (
            <ReportScrollableSection id="RMIntroSeg">
                <RetailMatchIntroAddSiteDetails
                    job={job}
                    showAddSiteDetails={showAddSiteDetails}
                    setShowAddSiteDetails={setShowAddSiteDetails}
                />
                <RetailMatchIntroAddPhoto
                    job={job}
                    visible={showAddSitePhotos}
                    setVisible={setShowAddSitePhotos}
                />
                <RetailMatchIntroEditBrandLogo
                    job={job}
                    brand={props.brand}
                    visible={showEditLogo}
                    setVisible={setShowEditLogo}
                />
                <ReportCard>
                    <RetailMatchIntroGrid>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                flexDirection: "column",
                                width: "100%",
                            }}
                        >
                            {brandId && (
                                <ReportBackLink
                                    text="Back to Overview"
                                    hide={false}
                                    onClick={onBackToOverviewClick}
                                />
                            )}
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                {!isSharing && (
                                    <>
                                        {!props.multiBrand && (
                                            <SiteDetailPhotoButtons
                                                title={"Edit Logo"}
                                                icon={<FiEdit3 height={"16px"} />}
                                                clickCallback={() => setShowEditLogo(true)}
                                                style={{
                                                    marginRight: "12px",
                                                }}
                                            />
                                        )}
                                        <SiteDetailPhotoButtons
                                            title={"Add Photo"}
                                            icon={<FiCamera height={"16px"} />}
                                            clickCallback={() => setShowAddSitePhotos(true)}
                                            style={{
                                                marginRight: "12px",
                                            }}
                                        />
                                        <SiteDetailPhotoButtons
                                            title="Add Site Details"
                                            icon={<FiEdit2 height={"16px"} />}
                                            clickCallback={() => setShowAddSiteDetails(true)}
                                        />
                                    </>
                                )}
                            </div>
                            <RetailMatchIntroBrandingContainer
                                onClick={() => setShowCarouselModal(true)}
                            >
                                <RetailMatchIntroBrandLogoWrapper>
                                    <RetailMatchIntroBrandLogo
                                        style={props.multiBrand ? { backgroundSize: "auto" } : null}
                                        background={
                                            props.multiBrand ? buxtonLogo : props.brand.logoPath
                                        }
                                    />
                                </RetailMatchIntroBrandLogoWrapper>
                                <ReportH3 style={{ padding: "0 2rem" }}>
                                    Know the potential for
                                    <br /> any brand at any property.
                                </ReportH3>
                            </RetailMatchIntroBrandingContainer>
                            <RetailMatchIntroPhotoCarousel
                                showCarouselModal={showCarouselModal}
                                setShowCarouselModal={setShowCarouselModal}
                                images={job.images}
                            />
                        </div>
                        <IntroGridContentBlockOne>
                            <RetailMatchIntroText
                                multiBrand={props.multiBrand}
                                jobResult={props.jobResult}
                                address={props.locationAddress}
                                overview={chartData.overview}
                                isSharing={isSharing}
                                benchmarkAddressOverride={props.benchmarkAddressOverride}
                                brand={props.brand}
                                renderType={props.renderType}
                            />
                        </IntroGridContentBlockOne>
                    </RetailMatchIntroGrid>
                </ReportCard>
            </ReportScrollableSection>
        );
    };
